import cx from "classnames";
import { useEffect, useRef, useState } from "react";

import * as StateLib from "@/Utils/StateLib";
import ActionListItem from "../../../../Composites/ActionListItem/ActionListItem";
import FiltersHighlightsEditModal from "./FiltersHighlightsEditModal";
import SectionCard, { SectionCardGroup } from "../../../../Composites/SectionCard/SectionCard";
import {
  Button,
  ColorPickerControl,
  Flex,
  List,
  Text,
  Checkbox,
  Alert
} from "@theorgchartdev/storybook";
import { ImportFiltersSection } from "./ImportFilters/ImportFilterSection";
import { getLocalizedColorPickerStrings } from "@/Utils/Utils";
import { appstr } from "@coreapp/utils/appstr";
import { Globals } from "@coreapp/CoreApp/Globals";
import { getFeatureAccess } from "@/Utils/Misc";
import { enumFeaturesList } from "@coreapp/constants";
import { IMPORT_FILTERS_TEST_PREFIX } from "./ImportFiltersUtils";

const BuildFilterOptions = (filters, onChange) => {
  let filterOptions = [];
  if (!filters) return filterOptions;

  for (const [aFilterIdx, filter] of filters.entries()) {
    let filterName = filter["filterName"];
    let filterDef = filter["filterDef"];
    if (!filterName) break;
    let filterOption = {};
    const dataTestId = `cs-filters-list-${aFilterIdx}`;
    const label = (
      <Flex justifyContent={"space-between"} alignItems={"center"} gap={"12px"}>
        <Text>{filterName}</Text>
        <Text colorModifier={"faintgrey"} modifier={"italic"}>
          {filterDef}
        </Text>
      </Flex>
    );
    filterOption.content = (
      <ActionListItem
        dataTestId={dataTestId}
        padding="medium"
        contentBefore={label}
        actions={[
          {
            iconName: "pen-to-square",
            tooltipProps: { message: appstr("Edit filter") },
            onClick: () => onChange("filters", "edit", aFilterIdx),
            dataTestId: `${dataTestId}-edit`
          },
          {
            iconName: filter.fFilterOnByDefault ? "star-solid" : "star-regular",
            tooltipProps: {
              message: filter.fFilterOnByDefault
                ? appstr("Turn off by default")
                : appstr("Turn on by default")
            },
            iconProps: { color: "brand" },
            onClick: () => onChange("filters", "default-on", aFilterIdx)
          },
          {
            iconName: filter.fHidden ? "eye" : "eye-slash",
            tooltipProps: {
              message: filter.fHidden ? appstr("Show filter") : appstr("Hide filter")
            },
            onClick: () => onChange("filters", "hide", aFilterIdx)
          },
          {
            iconName: "trash-can",
            tooltipProps: { message: appstr("Delete filter") },
            iconProps: { color: "error" },
            onClick: () => onChange("filters", "delete", aFilterIdx),
            dataTestId: `${dataTestId}-delete`
          }
        ]}
      />
    );
    filterOption.padding = false;
    filterOption.value = filter;
    filterOptions.push(filterOption);
  }
  return filterOptions;
};

const BuildHighlightOptions = (highlights, onChange) => {
  let highlightOptions = [];
  if (!highlights) return highlightOptions;

  for (const [aHighlightIdx, theHighlight] of highlights.entries()) {
    var highlightName = theHighlight["name"];
    var highlightRule = theHighlight["rule"];
    var highlightColor = theHighlight["color"];
    if (!highlightName) break;
    let highlightOption = {};
    const label = (
      <Flex justifyContent={"space-between"} alignItems={"center"} gap={"12px"}>
        <ColorPickerControl
          onChange={() => console.log("Update highlight color")}
          currentColor={highlightColor}
          textTags={getLocalizedColorPickerStrings()}
        />
        <Text>{highlightName}</Text>
        <Text colorModifier={"faintgrey"} modifier={"italic"}>
          {highlightRule}
        </Text>
      </Flex>
    );
    highlightOption.content = (
      <ActionListItem
        dataTestId={`cs-highlights-list-${aHighlightIdx}`}
        padding="medium"
        contentBefore={label}
        actions={[
          {
            iconName: "pen-to-square",
            tooltipProps: { message: appstr("Edit highlight") },
            onClick: () => onChange("highlights", "edit", aHighlightIdx)
          },
          {
            iconName: theHighlight.fHighlightOnByDefault ? "star-solid" : "star-regular",
            tooltipProps: {
              message: theHighlight.fHighlightOnByDefault
                ? appstr("Turn off by default")
                : appstr("Turn on by default")
            },
            iconProps: { color: "brand" },
            onClick: () => onChange("highlights", "default-on", aHighlightIdx)
          },
          {
            iconName: theHighlight.fHidden ? "eye" : "eye-slash",
            tooltipProps: {
              message: theHighlight.fHidden ? appstr("Show highlight") : appstr("Hide highlight")
            },
            onClick: () => onChange("highlights", "hide", aHighlightIdx)
          },
          {
            iconName: "trash-can",
            tooltipProps: { message: appstr("Delete highlight") },
            iconProps: { color: "error" },
            onClick: () => onChange("highlights", "delete", aHighlightIdx)
          }
        ]}
      />
    );
    highlightOption.padding = false;
    highlightOption.value = theHighlight;
    highlightOptions.push(highlightOption);
  }
  return highlightOptions;
};

export const ImportFiltersConfiguration = ({ chartConfig, handleUpdate }) => {
  let wsMgr = StateLib.GetWSMgr();
  const eventTracker = wsMgr.GetUsageTracker();

  useEffect(() => {
    eventTracker.ReportEvent("chart_settings.import_filters.opened");
  }, []);

  const [showModal, setShowModal] = useState(false);
  const [showFilter, setShowFilter] = useState(false);
  const [showHighlight, setShowHighlight] = useState(false);
  const [fhFields, setFhFields] = useState(null);

  const importFilterRef = useRef(null);

  if (!chartConfig) return;

  const filterList = chartConfig.filters || [];
  const highlightList = chartConfig.highlights || [];
  const hasLegacyFiltersAccess = getFeatureAccess(enumFeaturesList.savedFilters);
  const hasLegacyHighlightsAccess = getFeatureAccess(enumFeaturesList.savedHighlights);
  const showLegacyFilters = hasLegacyFiltersAccess && filterList.length > 0;
  const showLegacyHighlights = hasLegacyHighlightsAccess && highlightList.length > 0;
  const showIFIHSection = showLegacyFilters || showLegacyHighlights;

  let importFilterLimit = getFeatureAccess(enumFeaturesList.importFilterLimit);
  importFilterLimit = chartConfig.import.importFilters?.length >= importFilterLimit;

  const onChange = (mode, action, idx) => {
    switch (mode) {
      case "filters":
        switch (action) {
          case "edit":
            setShowModal(true);
            setShowHighlight(false);
            setShowFilter(true);
            setFhFields(idx);
            break;
          case "create":
            setShowModal(true);
            setShowHighlight(false);
            setShowFilter(true);
            setFhFields(null);
            break;
          case "delete":
            chartConfig["filters"].splice(idx, 1);
            handleUpdate(chartConfig, "filters");
            break;
          case "hide":
            var aFilter = chartConfig["filters"][idx];
            aFilter.fHidden = !aFilter.fHidden;
            handleUpdate(chartConfig, "filters");
            break;
          case "default-on":
            var aFilter = chartConfig["filters"][idx];
            aFilter.fFilterOnByDefault = !aFilter.fFilterOnByDefault;
            handleUpdate(chartConfig, "filters");
            break;
          default:
            break;
        }
        break;
      case "highlights":
        switch (action) {
          case "edit":
            setShowModal(true);
            setShowHighlight(true);
            setShowFilter(false);
            setFhFields(idx);
            break;
          case "create":
            setShowModal(true);
            setShowHighlight(true);
            setShowFilter(false);
            setFhFields(null);
            break;
          case "delete":
            chartConfig["highlights"].splice(idx, 1);
            handleUpdate(chartConfig, "highlights");
            break;
          case "hide":
            var aHighlight = chartConfig["highlights"][idx];
            aHighlight.fHidden = !aHighlight.fHidden;
            handleUpdate(chartConfig, "highlights");
            break;
          case "default-on":
            var aHighlight = chartConfig["highlights"][idx];
            aHighlight.fHighlightOnByDefault = !aHighlight.fHighlightOnByDefault;
            handleUpdate(chartConfig, "highlights");
            break;
          default:
            break;
        }
        break;
      default:
        break;
    }
  };

  const handleToggleDuplicates = () => {
    const nextChartConfig = {
      ...chartConfig,
      import: {
        ...chartConfig.import,
        dropDuplicates: !chartConfig["import"]["dropDuplicates"]
      }
    };
    handleUpdate(nextChartConfig, "import");
  };

  const closeEditModal = () => {
    setShowModal(false);
  };
  return (
    <SectionCardGroup>
      {showModal && (
        <FiltersHighlightsEditModal
          chartConfig={chartConfig}
          show={showModal}
          onClose={closeEditModal}
          showHighlight={showHighlight}
          showFilter={showFilter}
          edit={fhFields}
          fieldIdx={fhFields}
          handleUpdate={handleUpdate}
        />
      )}
      <SectionCard title={appstr("General")}>
        <Checkbox
          id="drop-duplicates-chkbx"
          label={appstr("dropdups")}
          size="medium"
          checked={chartConfig.import?.dropDuplicates == true}
          onToggle={handleToggleDuplicates}
          tooltipProps={{ message: appstr("duplicatedrop"), position: "bottom-start" }}
          data-testid={`${IMPORT_FILTERS_TEST_PREFIX}-drop-duplicates`}
        />
      </SectionCard>
      <SectionCard
        title={appstr("viewerdatafilters")}
        description={appstr("importfiltersdesc")}
        titleContent={
          <Button
            dataTestId="cs-import-filter-create"
            className={cx("oc-special-secondary", "oc-special-secondary--medium")}
            iconProps={{ name: "plus" }}
            modifier={"secondary"}
            onClick={() => {
              importFilterRef.current?.handleCreate();
            }}
            disabled={importFilterLimit}
          >
            {appstr("Create new import filter")}
          </Button>
        }
      >
        <ImportFiltersSection
          handleRef={importFilterRef}
          chartConfig={chartConfig}
          handleUpdate={handleUpdate}
        />
      </SectionCard>
      {showIFIHSection && (
        <Alert severity={"info"} showIcon>
          <Flex direction="column" gap="8px">
            <Text
              modifier="bold"
              colorModifier="info"
              className="ocn-import-filters-section-ifih-alert-title"
            >
              {appstr("Now available: IF/IH")}
            </Text>
            <Text size="small">
              {appstr("Now available: IF/IH detail").split("{link}")[0]}
              <a
                href="https://theorgchart.zendesk.com/hc/en-us/articles/28203844376212-Interactive-filters-and-highlights"
                target="_blank"
                rel="noreferrer"
                style={{ fontWeight: "bold", textDecoration: "underline", color: "inherit" }}
                data-testid={`${IMPORT_FILTERS_TEST_PREFIX}-ifih-alert-link`}
              >
                {appstr("Interactive filters and highlights")}
              </a>
              {appstr("Now available: IF/IH detail").split("{link}")[1]}
            </Text>
          </Flex>
        </Alert>
      )}
      {showLegacyFilters && (
        <SectionCard
          title={appstr("Legacy filters")}
          description={appstr("Create filters that can be shared with other users")}
        >
          <List
            enableVirtualization={false}
            listWidth="fill"
            options={BuildFilterOptions(filterList, onChange)}
            showCheck={false}
          />
        </SectionCard>
      )}
      {showLegacyHighlights && (
        <SectionCard
          title={appstr("Legacy highlights")}
          description={appstr("Create highlights that can be shared with other users")}
        >
          <List
            enableVirtualization={false}
            listWidth="fill"
            options={BuildHighlightOptions(highlightList, onChange)}
            showCheck={false}
          />
        </SectionCard>
      )}
    </SectionCardGroup>
  );
};
